import _isEmpty from 'lodash/isEmpty'
import _uniq from 'lodash/uniq'
import _omit from 'lodash/omit'
import { styled } from '@mui/material'
import currency from 'currency.js'
import giftCardImage from '@assets/images/gift-cards.jpg'
import FortegraModal from '@shared/modals/fortegra-modal'
import GuardianModal from '@shared/modals/guardian-modal'
import BedsToGoModal from '@shared/modals/bedstogo-modal'
import CasperModal from '@shared/modals/casper-modal'
import { fetchProductWarehouseAvailability, fetchProductBySku } from '@services/product'
import { getLocation } from '@services/location'
import { decodeTrademarkEntity, isHTML, months } from '@helpers/string-helper'
import { getSavedCouponType, hasSavedCoupon } from '@services/coupon'
import { isHexColorValid } from '@helpers/hexColor'
import { differenceInCalendarDays } from 'date-fns'
import { getRegionZone, getCurrentLocation, getZip } from './geo-location'
import { saveLocalStorage, getFromBrowserStorage } from './storage'
import { getDateFull } from './date'
import { isServiceContract } from './extendedServiceContract'

/**
 * Product bed types (STRING)
 * @type Array<string>
 */
export const BED_TYPES = ['bed', 'twinbed', 'fullbed']

/**
 * Product delivery types (ENUM)
 * @type Record<string, string>
 */
export const DELIVERY_TYPES = {
  O: 'O',
  U: 'U',
  D: 'D',
}

/**
 * Determines if the current location is in an undeliverable state
 * @returns {boolean}
 */
export const isUndeliverableState = ssrLocation => {
  const rtg_location = ssrLocation || getCurrentLocation()
  if (rtg_location?.state && ['Alaska', 'Hawaii', 'California'].includes(rtg_location.state)) {
    return true
  }
  return false
}

/**
 * Validates availability of a given product for the current region
 * @param {object} product
 * @returns {boolean}
 */
export const productAvailability = (product, inputRegion) => {
  let { region } = getRegionZone()
  region = inputRegion || region
  const { catalog_availability, availability, delivery_type } = product || {}

  if (isUndeliverableState()) {
    return false
  }

  if (
    (catalog_availability || !_isEmpty(availability)) &&
    region &&
    delivery_type !== DELIVERY_TYPES.O &&
    delivery_type !== DELIVERY_TYPES.U
  ) {
    return catalog_availability?.[region] || availability?.[region] || false
  }

  if (delivery_type === DELIVERY_TYPES.O || delivery_type === DELIVERY_TYPES.U) {
    return true
  }

  return false
}

export const weeksBetween = (date1, date2) => {
  const ONE_WEEK = 1000 * 60 * 60 * 24 * 7
  const difference_ms = Math.abs(date1.getTime() - date2.getTime())
  return Math.floor(difference_ms / ONE_WEEK)
}

export const daysBetween = (date1, date2) => {
  const ONE_DAY = 24 * 60 * 60 * 1000
  const difference_ms = Math.abs(date1.getTime() - date2.getTime())
  return Math.floor(difference_ms / ONE_DAY)
}

export function hasThankyouDiscount(product, rtg_location) {
  let { region, zone } = getRegionZone(rtg_location)
  if (product?.delivery_type === 'O' && region === 'OOM') {
    region = 'FL'
    zone = '0'
  }
  return (
    hasSavedCoupon() &&
    getSavedCouponType() !== 'OFF' &&
    product?.thank_you_pricing &&
    product?.thank_you_pricing?.[`${region}_${zone}`]
  )
}

export function productPrice(product, strikethrough = false, regionInput = '', zoneInput = '', forceDefault = false) {
  if (!product) return -1

  const { pricing, default_price, thank_you_pricing, delivery_type } = product

  if (forceDefault) {
    return pricing.default_price ?? default_price
  }

  let { region, zone } = getRegionZone()
  region = regionInput || region
  zone = zoneInput || zone
  if (delivery_type === 'O' && region === 'OOM') {
    region = 'FL'
    zone = '0'
  }

  // thank you coupon
  if (hasSavedCoupon() && getSavedCouponType() !== 'OFF' && thank_you_pricing) {
    let defaultPrice =
      product?.[`${region}_${zone}_list_price`] ||
      product?.[`zone_${zone}_list_price`] ||
      product?.price ||
      product?.default_price
    if (pricing) {
      // FD-4970 make sure to use previous default as some Thank You wont have pricing?.[`${region}_${zone}_list_price`] || pricing?.default_price.
      // so we need to use the previous default price
      defaultPrice = pricing?.[`${region}_${zone}_list_price`] || pricing?.default_price || defaultPrice
    }
    const thankyouPrice = strikethrough ? defaultPrice : thank_you_pricing[`${region}_${zone}`] || defaultPrice
    return thankyouPrice
  }
  if (pricing) {
    const region_zone = `${region}_${zone}`
    const salePrice = pricing[`${region_zone}_sale_price`] || pricing.salePrice
    const listPrice = pricing[`${region_zone}_list_price`] || pricing.listPrice
    const price = salePrice && !strikethrough ? salePrice : listPrice
    return price || pricing.default_price
  }

  const price =
    product[`zone_${zone}_sale_price`] && !strikethrough
      ? product[`zone_${zone}_sale_price`]
      : product[`zone_${zone}_list_price`]
  return price || default_price || product.price || 0
}

export function getWarrantyDataByRegionZone(warrantyOptions, regionZoneData) {
  if (warrantyOptions && regionZoneData) {
    const warrantyData = warrantyOptions[`${regionZoneData.region}_${regionZoneData.zone}`]
    return warrantyData ? { ...warrantyData, isServiceContract: isServiceContract(warrantyData.provider_name) } : null
  }
  return null
}

export function getSelectionPricing(pricing) {
  const { region, zone } = getRegionZone()
  return pricing ? pricing[`${region}_${zone}_list_price`] : 0
}

export function getWarrantyModalByProvider(warrantyData) {
  if (warrantyData && warrantyData.provider_name) {
    if (warrantyData.provider_name === 'GUARDIAN') return GuardianModal
    if (warrantyData.provider_name === 'FORTEGRA') return FortegraModal
  }
  return null
}

export function getSleepTrialModalByBrand(product) {
  if (product && product.brand) {
    if (product.brand.toLowerCase() === 'beds to go') return BedsToGoModal
    if (product.brand.toLowerCase() === 'casper') return CasperModal
  }
  return null
}

export function productOnSale(product, location) {
  if (!product) return false
  let { region, zone } = getRegionZone(location)
  if (product.delivery_type === 'O' && region === 'OOM') {
    region = 'FL'
    zone = '0'
  }
  return product.on_sale ? product.on_sale[`${region}_${zone}`] : product[`zone_${zone}_on_sale`]
}

/**
 * getTileDataWithBanners
 * factory function to decorate search result products with plpBanners
 * @param {*} hits
 * @param {*} plpBanners
 */
export const getTileDataWithBanners = (hits, plpBanners) => {
  if (!plpBanners || plpBanners?.length === 0) return hits
  const tiles = [...hits]
  plpBanners.forEach(plpBannerData => {
    if (plpBannerData?.Banner?.Banner) {
      const { Banner, TileIndex } = plpBannerData
      const tileIndex = TileIndex
      const plpBannerTileData = {
        tileIndex,
        isPLPBanner: true,
        Banner: Banner.Banner,
      }
      /*
      By setting tileIndex to tileIndex - 1, we are 0-indexing the tileIndex provided from Contentful.
      In Contentful, if tileIndex === 1, we want it to reference the 0th element in the tiles array
    */
      const zeroIndexedTileIndex = tileIndex - 1
      tiles.splice(zeroIndexedTileIndex, 0, plpBannerTileData)
    }
  })
  return tiles
}

/**
 * isProductStrikeThrough
 * Determine if a product is on clearance or not. If yes, display a strikethrough price.
 * @param {*} product
 * @returns {boolean}
 */
export const isProductStrikeThrough = product => {
  if (!product) return false

  let { region, zone } = getRegionZone()
  if (region) {
    const { thank_you_pricing = false, strikethrough, pricing, price, list_price, delivery_type } = product

    // FD-3069 - Related to previous Hotfix (FD-2784)
    if (delivery_type === 'O' && region === 'OOM') {
      region = 'FL'
      zone = '0'
    }

    /* check to see if we need to add a strikethrough for 'thank you' coupons */
    const hasCoupon = hasSavedCoupon()
    if (hasCoupon) {
      const couponType = getSavedCouponType()
      switch (couponType) {
        case 'OFF':
          break
        default:
          if (thank_you_pricing && thank_you_pricing[`${region}_${zone}`]) {
            return true
          }
          break
      }
    }
    return (
      strikethrough &&
      (strikethrough[region] || strikethrough === true) &&
      ((pricing && pricing[`${region}_${zone}_list_price`] !== pricing[`${region}_${zone}_sale_price`]) ||
        product[`zone_${zone}_sale_price`] !== product[`zone_${zone}_list_price`] ||
        price !== list_price)
    )
  }
  return false
}

// Memoization Example
// eslint-disable-next-line func-names
export const getRegionSkuList = (function() {
  const cache = {}
  return (list, region) => {
    const key = JSON.stringify([list, region])
    if (!cache[key]) {
      if (list) {
        if (region === 'FL' && list.FL) {
          cache[key] = list.FL
        }
        if (region === 'SE' && list.SE) {
          cache[key] = list.SE
        }
        if (region === 'TX' && list.TX) {
          cache[key] = list.TX
        }
        if (region === 'OOM' && list.OOM) {
          cache[key] = list.OOM
        }
      }
    }
    return cache[key]
  }
})()

export const availabilityStockMessage = (
  { availableOn, product, rtg_location, setStockMessage = null, useDeprecatedStockMessage = false },
  setProductAvailability,
) => {
  let availability = false
  let stockMessage

  if (!product) return null

  const enhancedStockMessageFunc = useDeprecatedStockMessage ? enhanceStockMessage : configStockMessage
  const today = new Date()
  const availableDate = new Date(availableOn)
  const availableSoonDateThreshold = new Date('2999-12-31T00:00:00.000Z')
  const weeks = weeksBetween(today, availableDate)
  const days = daysBetween(today, availableDate)
  const shipCostCode = product?.shipping_cost_code?.[rtg_location.region] || ''
  if (availableOn === 'Unavailable') {
    stockMessage = 'Unavailable '
  } else if (availableOn === '9999-12-31T00:00:00.000Z') {
    stockMessage = 'Discontinued '
  } else if (availableDate.getTime() >= availableSoonDateThreshold.getTime()) {
    stockMessage = 'Available Soon '
    availability = true
  } else if (product?.delivery_type === 'O' || product?.delivery_type === 'U') {
    availability = true
    if (availableDate.setHours(0, 0, 0, 0) > today.setHours(0, 0, 0, 0)) {
      stockMessage = `${product?.delivery_type === 'U' ? 'Ships via UPS.' : 'Ships direct from vendor.'}
      Item available after ${months[availableDate.getMonth()]} ${availableDate.getDate()}.`
    } else if (shipCostCode === 'LT') {
      stockMessage = `The carrier will contact you directly to schedule your delivery, which includes placing box into the first room of your home.`
    } else if (shipCostCode === 'ITEM' && product?.delivery_type === 'O') {
      stockMessage = `Ships direct from vendor. Allow 4-6 weeks for delivery.`
    } else if (shipCostCode === 'UD') {
      stockMessage = `Ships via UPS. Allow 7-10 days for delivery.`
    } else if (shipCostCode === 'UDR') {
      stockMessage = `Ships via UPS, usually within a week.`
    } else if (shipCostCode === 'VD23') {
      stockMessage = `Ships direct from vendor. Allow 2-3 weeks for delivery.`
    } else if (shipCostCode === 'VD35') {
      stockMessage = `Ships direct from vendor. Usually within a week.`
    } else if (shipCostCode === 'VD71') {
      stockMessage = `Ships direct from vendor, usually within 7 to 10 business days.`
    } else if (shipCostCode === 'VDEX') {
      stockMessage = `Ships direct from vendor, usually within 2 to 3 business days.`
    } else if (shipCostCode === 'VDR') {
      stockMessage = `Ships direct from vendor, usually within a week.`
    } else {
      stockMessage = `${product.delivery_type === 'U' ? 'Ships via UPS.' : 'Ships direct from vendor.'}`
    }
  } else if (product?.delivery_type !== 'O' && product?.delivery_type !== 'U' && rtg_location?.region === 'OOM') {
    stockMessage = 'Not available in your region '
  } else if (days > 120) {
    stockMessage = 'Usually available in 4-6 months '
    availability = true
  } else if (weeks >= 2) {
    stockMessage = `Available as soon as ${getDateFull(availableDate)} `
    availability = true
  } else {
    stockMessage = 'Available Now '
    availability = true
  }

  if (setProductAvailability) {
    setProductAvailability(availability)
  }

  if (setStockMessage && stockMessage) {
    setStockMessage(enhancedStockMessageFunc(stockMessage))
  }

  return enhancedStockMessageFunc(stockMessage)
}

export const availabilityAndStockMessage = (
  { availabilityData, product = {}, rtg_location, setStockMessage = null },
  setProductAvailability,
) => {
  /* if the product type is a room and the items are eligible for split delivery, show a unique message */
  if (
    Object.is(product?.type, 'room') &&
    availabilityData?.availabilities[0]?.splitDeliveryMessage &&
    availabilityData?.availabilities[0].catalog_availability[rtg_location.region]
  ) {
    return {
      stockMessage: configStockMessage(availabilityData.availabilities[0].splitDeliveryMessage),
      availability: true,
    }
  }
  let availability = false
  let stockMessage
  const isAvailableInRegion =
    availabilityData?.availabilities?.[0]?.catalog_availability?.[rtg_location.region] || false
  const availableOn = isAvailableInRegion ? availabilityData?.earliestAvailability || 'Unavailable' : 'Unavailable'
  const today = new Date()
  const availableDate = new Date(availableOn)
  const availableSoonDateThreshold = new Date('2999-12-31T00:00:00.000Z')
  const weeks = weeksBetween(today, availableDate)
  const days = daysBetween(today, availableDate)
  const shipCostCode = product?.shipping_cost_code?.[rtg_location.region] || ''

  if (availableOn === 'Unavailable') {
    stockMessage = 'Unavailable '
  } else if (availableOn === '9999-12-31T00:00:00.000Z') {
    stockMessage = 'Discontinued '
  } else if (product?.delivery_type !== 'O' && product?.delivery_type !== 'U' && rtg_location?.region === 'OOM') {
    stockMessage = 'Not available in your region '
  } else if (availableDate.getTime() >= availableSoonDateThreshold.getTime()) {
    stockMessage = 'Available Soon '
    availability = true
  } else if (product.delivery_type === 'O' || product.delivery_type === 'U') {
    availability = true
    if (availableDate.setHours(0, 0, 0, 0) > today.setHours(0, 0, 0, 0)) {
      stockMessage = `${product.delivery_type === 'U' ? 'Ships via UPS.' : 'Ships direct from vendor.'}
      Item available after ${months[availableDate.getMonth()]} ${availableDate.getDate()}.`
    } else if (shipCostCode === 'LT') {
      stockMessage = `The carrier will contact you directly to schedule your delivery, which includes placing box into the first room of your home.`
    } else if (shipCostCode === 'ITEM' && product.delivery_type === 'O') {
      stockMessage = `Ships direct from vendor. Allow 4-6 weeks for delivery.`
    } else if (shipCostCode === 'UD') {
      stockMessage = `Ships via UPS. Allow 7-10 days for delivery.`
    } else if (shipCostCode === 'UDR') {
      stockMessage = `Ships via UPS, usually within a week.`
    } else if (shipCostCode === 'VD23') {
      stockMessage = `Ships direct from vendor. Allow 2-3 weeks for delivery.`
    } else if (shipCostCode === 'VD35') {
      stockMessage = `Ships direct from vendor. Usually within a week.`
    } else if (shipCostCode === 'VD71') {
      stockMessage = `Ships direct from vendor, usually within 7 to 10 business days.`
    } else if (shipCostCode === 'VDEX') {
      stockMessage = `Ships direct from vendor, usually within 2 to 3 business days.`
    } else if (shipCostCode === 'VDR') {
      stockMessage = `Ships direct from vendor, usually within a week.`
    } else {
      stockMessage = `${product.delivery_type === 'U' ? 'Ships via UPS.' : 'Ships direct from vendor.'}`
    }
  } else if (days > 120) {
    stockMessage = 'Usually available in 4-6 months '
    availability = true
  } else if (weeks >= 2) {
    stockMessage = `Available as soon as ${getDateFull(availableDate)} `
    availability = true
  } else {
    stockMessage = 'Available Now '
    availability = true
  }

  if (setProductAvailability) {
    setProductAvailability(availability)
  }

  if (setStockMessage && stockMessage) {
    setStockMessage(configStockMessage(stockMessage))
  }

  return { stockMessage: configStockMessage(stockMessage), availability }
}

/**
 * Set product availablity synchronously if availability info is already available. Good for ssr pdps.
 *
 * @param product the product.json (SKU) you want to search
 * @returns {null|Promise<T>}
 */
export const getStockMessageSync = product => {
  let earliestAvailability
  let isAvailable
  let stockMessage
  /* get the location to check for region (SE, OOM, etc) */
  const rtg_location = getCurrentLocation()
  if (product?.delivery_type !== 'O' && product?.delivery_type !== 'U' && rtg_location?.region === 'OOM') {
    stockMessage = 'Not available in your region '
    isAvailable = false
  } else {
    stockMessage = configStockMessage('Available Now ')
  }
  if (
    product?.availabilityInfo &&
    product?.sku !== '83333333' &&
    (rtg_location?.region !== 'OOM' ||
      ((product?.delivery_type === 'O' || product?.delivery_type === 'U') && rtg_location?.region === 'OOM'))
  ) {
    /* call the warehouse availability */
    try {
      const { data } = product.availabilityInfo
      earliestAvailability = data?.earliestAvailability
      isAvailable = productIsAvailable(data)
      /* if the product type is a room and the items are eligible for split delivery, show a unique message */
      if (Object.is(product?.type, 'room') && data?.availabilities[0]?.splitDeliveryMessage && isAvailable) {
        stockMessage = configStockMessage(data.availabilities[0].splitDeliveryMessage)
      } else {
        /* otherwise, show the standard availability message */
        const isAvailableInRegion = data?.availabilities?.[0]?.catalog_availability?.[rtg_location.region] || false
        const availableOn = isAvailableInRegion ? data?.earliestAvailability || 'Unavailable' : 'Unavailable'
        stockMessage = availabilityStockMessage({ availableOn, product, rtg_location }, availability => {
          isAvailable = availability
        })
      }
    } catch (error) {
      /* standard error handling */
      console.error('Warehouse availability stock message', error)
      stockMessage = 'Out of Stock '
      isAvailable = false
    }
  }
  return { earliestAvailability, isAvailable, stockMessage }
}

// TODO: All occurrences of setProductAvailability should be pull out from getStockMessage
/**
 * this function calls the warehouse availability to determine if a product is in stock
 *
 * @param product the product.json (SKU) you want to search
 * @param setStockMessage the setState object you want the result to be posted to
 * @returns {null|Promise<T>}
 */
export const getStockMessage = (
  product,
  setStockMessage,
  setProductAvailability,
  fetchedProductWarehouseAvailability,
) => {
  /* get the location to check for region (SE, OOM, etc) */
  const rtg_location = getCurrentLocation()
  if (
    product?.sku !== '83333333' &&
    !isUndeliverableState() &&
    (rtg_location?.region !== 'OOM' ||
      ((product?.delivery_type === 'O' || product?.delivery_type === 'U') && rtg_location?.region === 'OOM'))
  ) {
    /* call the warehouse availability */
    return (fetchedProductWarehouseAvailability
      ? Promise.resolve({ data: fetchedProductWarehouseAvailability })
      : fetchProductWarehouseAvailability(product.sku, rtg_location.distribution_index, rtg_location.state)
    )
      .then(({ data }) => {
        /* if the product type is a room and the items are eligible for split delivery, show a unique message */
        if (
          Object.is(product?.type, 'room') &&
          data?.availabilities[0]?.splitDeliveryMessage &&
          data?.availabilities[0].catalog_availability[rtg_location.region]
        ) {
          if (setProductAvailability) {
            setProductAvailability(true)
          }

          setStockMessage(configStockMessage(data.availabilities[0].splitDeliveryMessage))
        } else {
          /* otherwise, show the standard availability message */
          const isAvailableInRegion = data?.availabilities?.[0]?.catalog_availability?.[rtg_location.region] || false

          const availableOn = isAvailableInRegion ? data?.earliestAvailability || 'Unavailable' : 'Unavailable'
          availabilityStockMessage({ availableOn, product, rtg_location, setStockMessage }, setProductAvailability)
        }
      })
      .catch(err => {
        /* standard error handling */
        console.error('Warehouse availability stock message', err)
        setStockMessage('Out of Stock ')
        if (setProductAvailability) {
          setProductAvailability(false)
        }
      })
  }
  if (
    (product?.delivery_type !== 'O' && product?.delivery_type !== 'U' && rtg_location?.region === 'OOM') ||
    isUndeliverableState()
  ) {
    setStockMessage('Not available in your region ')
    if (setProductAvailability) {
      setProductAvailability(false)
    }
  } else {
    setStockMessage(configStockMessage('Available Now '))
    if (setProductAvailability) {
      setProductAvailability(true)
    }
  }
  return null
}

export const productIsAvailable = availability => {
  const rtg_location = getCurrentLocation()
  return availability?.availabilities[0].catalog_availability[rtg_location.region]
}

export const getProductAvailableIn = (date, days = 60) => {
  const today = new Date()
  const earliestAvailabilityDate = new Date(date)
  const differenceInDays = (earliestAvailabilityDate.getTime() - today.getTime()) / (1000 * 3600 * 24)

  return differenceInDays < days
}

export const getRequiredAddon = requiredAddons => {
  let requiredAddon
  if (requiredAddons) {
    let title
    let decline
    let price = 0

    const skus = []

    requiredAddons.forEach((addon, i) => {
      if (addon.quantity === 1 && addon.title?.includes('Bunkie Board')) {
        // make bunkie board(s) singular or plural
        addon.title = addon.title.replace(/Boards/g, 'Board')
      }

      if (i > 0) {
        title = `${title} and ${addon.title}${addon.quantity > 1 ? ` (${addon.quantity})` : ''}`
      } else {
        title = `${addon.title}${addon.quantity > 1 ? ` (${addon.quantity})` : ''}`
      }

      decline = i > 0 ? `${decline} and ${addon.title}` : `${addon.title}`
      price += productPrice(addon) * addon.quantity
      skus.push(addon.sku)
    })

    requiredAddon = {
      title,
      price,
      skus,
      decline,
    }
  }
  return requiredAddon
}

/**
 * Checks if addonSku is present within the activeAddons array, which indicates the user has selected the addon item
 * @param {object[]} activeAddons array of selected addons
 * @param {string} addonSku SKU that is being checked for existence in activeAddons
 * @returns bool
 */
export const isActiveAddon = (activeAddons, addonSku) =>
  activeAddons ? activeAddons.some(activeAddon => activeAddon.sku === addonSku) : false

export const savePageScroll = () => {
  if (
    window.location.pathname.includes('/furniture') ||
    window.location.pathname.includes('/mattress') ||
    window.location.pathname.includes('/search')
  ) {
    saveLocalStorage('rtg_scroll', {
      scrollY: window.scrollY,
      pathForScroll: window.location.pathname,
    })
  }
}

export const scrollToPLP = () => {
  const rtg_scroll = getFromBrowserStorage('local', 'rtg_scroll')
  if (rtg_scroll && rtg_scroll.scrollY && rtg_scroll.pathForScroll) {
    if (window.location.pathname === rtg_scroll.pathForScroll) {
      window.scrollTo(0, rtg_scroll.scrollY)
    } else {
      saveLocalStorage('rtg_scroll', {})
    }
  }
}

export const getGiftCardProductData = () => ({
  sku: '83333333',
  delivery_type: 'T',
  price: 25,
  unitPrice: 25,
  title: 'Gift Card',
  primary_image: giftCardImage,
  grid_image: giftCardImage,
  category: 'gift-card',
  catalog: 'gift-card',
  pricing: { default_price: 25 },
})

export const getCustomGiftCardProductData = ({ quantity }) => ({
  sku: '83333333',
  delivery_type: 'T',
  price: 25 * quantity,
  unitPrice: 25 * quantity,
  title: 'Gift Card',
  primary_image: giftCardImage,
  grid_image: giftCardImage,
  category: 'gift-card',
  catalog: 'gift-card',
  pricing: { default_price: 25 * quantity },
})

export const fetchProductOrGC = (sku, product) => {
  if (sku === '83333333') {
    return new Promise(resolve =>
      resolve({
        ...getGiftCardProductData(),
        unitPrice: product?.price,
        price: product?.price,
        pricing: { default_price: product?.price },
      }),
    )
  }
  if (product?.product?.isConfigurable) {
    // M1 configurator imported SKUs do not have JSON to return from fetchProductBySku()
    return new Promise(resolve => resolve({ ...product.product }))
  }
  return fetchProductBySku(sku)
}

export const getProductImages = (product, isMobile = false) => {
  const {
    single_item_room,
    primary_image_item,
    primary_image_room,
    primary_image,
    alternate_images,
    app_image,
  } = product
  let images = []
  const roomImage = isMobile ? app_image : primary_image_room
  if (single_item_room) {
    if (primary_image_item) {
      images.push(primary_image_item)
    } else {
      images.push(primary_image)
    }
    if (roomImage) {
      images.push(roomImage)
    }
  } else {
    if (roomImage) {
      images.push(roomImage)
    } else {
      images.push(primary_image)
    }
    if (primary_image_item) {
      images.push(primary_image_item)
    }
  }
  if (alternate_images) {
    images = images.concat(
      alternate_images?.map?.(image => {
        if (isMobile && image?.includes?.('image-room')) {
          return app_image
        }

        return image
      }),
    )
  }

  return Array.from(new Set(_uniq(images.filter(image => !!image))))
}

export const getProductDisplayImage = (product = {}) => {
  const { single_item_room, primary_image_item, primary_image, primary_image_room, image } = product
  if (single_item_room) {
    return primary_image_item ?? primary_image_room
  }
  return primary_image || image
}

export const getImagesForProductTile = ({
  type,
  app_image,
  single_item_room,
  primary_image_item,
  primary_image,
  alternate_images,
  primary_image_room,
  dimension_image,
}) => {
  // Rooms vs Single room items
  const selectedImages = []
  let alternateImages = alternate_images || []
  if (dimension_image) {
    alternateImages = alternate_images.filter(image => image !== dimension_image)
  }
  // weird lint rule
  const zeroIndex = 0
  if (type === 'room') {
    if (single_item_room) {
      // sigle item room
      // https://s3.amazonaws.com/dev-roomstogo-products/1071635P.json
      selectedImages[0] = app_image || primary_image || primary_image_item
      selectedImages[1] = null
    } else {
      // Room
      // https://s3.amazonaws.com/dev-roomstogo-products/1900863P.json
      selectedImages[0] = app_image || primary_image_room || primary_image
      selectedImages[1] = null
    }
  } else if (type === 'item' && single_item_room === false) {
    // Single items
    // https://s3.amazonaws.com/dev-roomstogo-products/42257849.json
    selectedImages[0] = primary_image_item || primary_image
    // TODO: Once altImages have correct aspect ratio, put back; backend fix
    // selectedImages[1] = alternateImages[zeroIndex]
    selectedImages[1] = app_image || null
  } else {
    // here it goes ALL items that are
    // type != 'room'
    // type == 'item' && single_item_room == true
    // type != 'item'
    // do we have this cases ? how do we show images ?
    // TODO: Check this cornercase, what images we display here ?
    selectedImages[0] = primary_image
    selectedImages[1] = alternateImages[zeroIndex]
  }

  return selectedImages
}

/* unsure if this function is used anywhere */
export const sortByPriceAscending = products => {
  if (products != null) {
    return products.slice().sort((a, b) => productPrice(a) - productPrice(b))
  }
  return products
}

export const sortByPriceDescending = products => {
  if (products != null) {
    return products.slice().sort((a, b) => productPrice(b) - productPrice(a))
  }
  return products
}

export const earliestStockDate = (product, distIndex) => {
  // this is the newer way to check availability
  if (distIndex && product?.availabilityByDistIndex?.[distIndex]?.availabilityDate) {
    return product.availabilityByDistIndex[distIndex].availabilityDate
  }

  // this method may be deprecated
  if (product?.warehouseAvailability?.data?.earliestAvailability) {
    return product.warehouseAvailability.data.earliestAvailability
  }

  return ''
}

/* this is what will be shown to the user if not overridden */
export const SaleFlagType = {
  BonusBuy: 'BONUS BUY',
  Closeout: 'LAST CHANCE',
  SmallSpaces: 'SMALL SPACES', // Disabled
  Sale: 'SALE',
  InStock: 'IN STOCK',
  InStockAndOnSale: 'IN STOCK & ON SALE',
  InStockAndSave: 'IN STOCK & SAVE',
  NewNow: 'NEW',
  None: '',
  Save: 'SAVE',
  Coupon: 'COUPON',
}

/**
 * what should we display in the sale flag
 *
 * @param {{product, availabilityDate:Date, sale:boolean }} args
 * @returns {typeof SaleFlagType[keyof SaleFlagType]}
 */
export function getProductSaleFlagType({ product = {}, availabilityDate, sale }, allowInStockSaleFlag = true) {
  const { region, zone } = getRegionZone()

  const price = productPrice(product)
  const checkCoupon = () => {
    //  TODO check to ensure that couponDisplayFields.[region].promoType !== 'BONUS BUY'?
    //  TODO why does product s3 have region but this local does not?
    if (typeof product.couponDisplayFields === 'object' && Object.keys(product.couponDisplayFields).length > 0) {
      return true
    }
    return false
  }

  const checkBonusBuy = () => {
    /* only show flag if bonus buy offer meets criteria */
    const productPromotion =
      product?.promotions?.[`${region}_${zone}`] ||
      product?.promotions?.[`${region}_0`] ||
      product?.promotions?.default_promotion
    return productPromotion?.qualifier_value < price
  }

  // const checkSmallSpaces = () => {
  //   const { theme } = product || {}
  //   return theme?.length && theme.includes('condo')
  // }

  const checkCloseout = () =>
    /* only show flag if closeout meets criteria */
    (product?.closeout?.[`${region}`] && Object.is(typeof product?.closeout, 'object')) ||
    (product?.closeout && !Object.is(typeof product?.closeout, 'object'))

  /*  if PDP we'll use the prop, if PLP we'll grab date from algolia product */
  const checkInStock = date =>
    weeksBetween(new Date(date), new Date()) < 2 || (date?.includes && date.includes('Available Now'))

  const checkNewProduct = prod => prod?.newNow

  /* this is the order of priority, bonus buy displays before closeout, etc... */
  const customFlag = getCustomSaleFlag(product)
  if ((checkCoupon() && checkBonusBuy()) || customFlag?.text?.toLowerCase().includes('bonus'))
    return SaleFlagType.BonusBuy
  if (customFlag) return customFlag.text
  if (checkCoupon()) return SaleFlagType.Coupon
  if (checkBonusBuy()) return SaleFlagType.BonusBuy
  if (checkCloseout()) return SaleFlagType.Closeout
  if (
    (productOnSale(product) || sale) &&
    checkInStock(availabilityDate || product?.availabilityDate) &&
    allowInStockSaleFlag
  )
    return product?.brand === 'tempur-pedic' ? SaleFlagType.InStockAndSave : SaleFlagType.InStockAndOnSale
  if (productOnSale(product) || sale) return product?.brand === 'tempur-pedic' ? SaleFlagType.Save : SaleFlagType.Sale
  if (checkNewProduct(product)) return SaleFlagType.NewNow
  // if (checkSmallSpaces()) return SaleFlagType.SmallSpaces
  if (checkInStock((availabilityDate || product?.availabilityDate) && allowInStockSaleFlag)) return SaleFlagType.InStock
  return SaleFlagType.None
}

/* Custom Sale Flag */
export const CustomSaleFlagData = {
  text: '',
  bgColor: '',
  textColor: '',
}

/**
 *
 * @param {product: object} args
 * @returns {typeof CustomSaleFlagData || null}
 */
export const getCustomSaleFlag = product => {
  const { region } = getRegionZone()
  const customSaleFlagAlgolia = product?.saleFlag?.text ? product?.saleFlag : null
  const customSaleFlagSource = product?.saleFlag?.[region]?.text ? product.saleFlag[region] : null
  const customSaleFlag = customSaleFlagAlgolia || customSaleFlagSource
  if (!customSaleFlag) return null
  return {
    text: customSaleFlag.text,
    bgColor: isHexColorValid(customSaleFlag['background-color']) ? customSaleFlag['background-color'] : null,
    textColor: isHexColorValid(customSaleFlag['text-color']) ? customSaleFlag['text-color'] : null,
  }
}

/**
 *
 * @param {{delivery_sub_type_code, vendorId }} args
 * @returns {typeof boolean}
 */
export const renderLTLmessage = async ({ delivery_sub_type_code, vendorId }) => {
  if (delivery_sub_type_code !== 'LT') return false
  const location = await getLocation(getZip())
  return Object.is(location?.response?.ltlVendors, vendorId)
}

/**
 * @deprecated use configStockMessage instead
 */
export const enhanceStockMessage = message => {
  // todo maybe it's good time to refactor the way stockMessages are passed
  // since we're using this function elsewhere, its easiest to keep the styles inline
  const upsDelivery = 'Ships via UPS'
  const availableAsSoonAs = 'Available as soon as'
  const availableNow = 'Available Now'
  const availableAfter = 'Item available after'
  const vendorDirect = 'Ships direct from vendor.'
  const splitDelivery = 'Some items in this room may be'

  if (message?.includes(splitDelivery)) {
    /* sometimes the split delivery can include the phrase 'Available Now', so we just return the full formatted message */
    return message
  }

  if (message?.includes(availableAsSoonAs)) {
    const split = message.split(availableAsSoonAs)
    return `${availableAsSoonAs}<span style="font-weight: bold">${split[1]}</span>`
  }

  if (message?.includes(availableNow)) {
    return `<span style="font-weight: bold">${message}</span>`
  }
  // pulls the UPS delivery message. After, we call this function recursively to enhance the stock message.
  if (message?.includes(upsDelivery)) {
    const split = message.split(upsDelivery)
    return `<p style="font-style: normal; line-height: 20px; font-size: 15px;" >${upsDelivery}</p><span>${configStockMessage(
      split[1],
    )}</span>`
  }
  // this just pulls off the vendor direct message. After, we call this function recursively to enhance the stock message.
  if (message?.includes(vendorDirect)) {
    const split = message.split(vendorDirect)
    return `<p style="font-style: normal; line-height: 20px; font-size: 15px;" >${vendorDirect}</p><span>${configStockMessage(
      split[1],
    )}</span>`
  }
  if (message?.includes(availableAfter) && !message?.includes(vendorDirect)) {
    const split = message.split(availableAfter)
    return `<span style="color: #333;">Available after</span><span style="font-weight: bold">${split[1]}</span>`
  }

  return message
}

export const configStockMessage = message => {
  const upsDelivery = 'Ships via UPS'
  const availableAsSoonAs = 'Available as soon as'
  const availableNow = 'Available Now'
  const availableAfter = 'Item available after'
  const vendorDirect = 'Ships direct from vendor.'
  const splitDelivery = 'Some items in this room may be'

  const StockMessageContainer = styled('div')({
    '& > *': {
      fontSize: '0.875rem !important',
    },
  })

  const StockMessageTextSpan = styled('span')({
    fontWeight: 'bold',
  })

  const StockMessageTextSpan2 = styled('span')({
    color: '#333',
  })

  const StockMessageAvailable = styled(StockMessageTextSpan)(({ theme }) => ({
    color: theme.palette.success.main,
  }))

  const StockMessageTextParagraph = styled('p')({
    fontStyle: 'normal',
    lineHeight: '20px',
    fontSize: '15px',
  })

  if (isHTML(message)) {
    return message
  }

  if (message?.includes(splitDelivery)) {
    /* sometimes the split delivery can include the phrase 'Available Now', so we just return the full formatted message */
    return [
      {
        message,
        Component: StockMessageContainer,
      },
    ]
  }

  if (message?.includes(availableAsSoonAs)) {
    const split = message.split(availableAsSoonAs)
    return [
      {
        message: availableAsSoonAs,
        Component: StockMessageContainer,
      },
      {
        message: split[1],
        Component: StockMessageAvailable,
      },
    ]
  }

  if (message?.includes(availableNow)) {
    return [
      {
        message,
        Component: StockMessageAvailable,
      },
    ]
  }
  // pulls the UPS delivery message. After, we call this function recursively to enhance the stock message.
  if (message?.includes(upsDelivery)) {
    const split = message.split(upsDelivery)
    return [
      {
        message: upsDelivery,
        Component: StockMessageTextParagraph,
      },
      ...(split?.[1].replace(/[^a-zA-Z0-9 ]/g, '')?.length > 0 ? configStockMessage(split?.[1]) : []),
    ]
  }
  // this just pulls off the vendor direct message. After, we call this function recursively to enhance the stock message.
  if (message?.includes(vendorDirect)) {
    const split = message.split(vendorDirect)
    return [
      {
        message: vendorDirect,
        Component: StockMessageTextParagraph,
      },
      ...configStockMessage(split[1]),
    ]
  }
  if (message?.includes(availableAfter) && !message?.includes(vendorDirect)) {
    const split = message.split(availableAfter)
    return [
      { message: 'Available after', Component: StockMessageTextSpan2 },
      { message: split[1], Component: StockMessageTextSpan },
    ]
  }

  return [
    {
      message,
      Component: StockMessageContainer,
    },
  ]
}

/**
 * Determines whether Add to Cart button should render on PLP Grid items, inspired by vendor products available to OOM customers
 * @param {string} region
 * @param {string} delivery_type
 * @returns {boolean}
 */
export const displayAddToCartButton = ({ region, delivery_type }) => {
  if (delivery_type === 'O' || delivery_type === 'U') return true
  if (region === 'OOM') return false
  return true
}

export const renderSeeInStore = ({ delivery_type, region }) =>
  delivery_type !== 'O' && delivery_type !== 'U' && !Object.is(region, 'OOM')

/**
 * Filters product.json variations and returns array of available variations based on region/delivery type
 * @param {[object]} variations
 * @param {string} productSku
 * @returns {object} []
 */

export function filterVariationsByAvailability({ variations, productSku, region, zone }) {
  const availableVariations = variations
    ? variations
        .filter(v => productAvailability(v, region) || !v.catalog_availability)
        ?.map(variation => ({
          ...variation,
          pdp_price: productPrice(variation, false, region, zone),
        }))
        .sort((a, b) => {
          if (a.variation_value < b.variation_value) {
            return -1
          }
          if (a.variation_value > b.variation_value) {
            return 1
          }
          return 0
        })
    : null

  if (!availableVariations) return null
  return availableVariations
}

/**
 * Determines whether a product meets criteria to display PDP Right Rail components or legacy PDP
 * @param {string} category
 * @param {string} type
 * @param {bool} single_item_room
 * @returns {bool}
 */
export const displayLivingRoom = ({ category, type, single_item_room }) =>
  category?.match('livingroom') && (type === 'item' || (single_item_room === true && type === 'room'))

/**
 * Returns sorted size variation according to weightened values
 * @param {Array} sizeVariations
 * @returns {Array}
 */
export const sortMattressSizeVariations = sizeVariations => {
  // Size options should be sorted acording to value weight in this object
  // higher numbers has more weight, so goes down into the list.
  const optionWeight = {
    twin: 1,
    'twin xl': 2,
    full: 3,
    queen: 4,
    king: 5,
    'split king': 6,
    'california king': 7,
    'split california king': 8,
  }

  return sizeVariations.sort((a, b) => {
    const aWeight = optionWeight[a.variation_value.toLowerCase()]
    const bWeight = optionWeight[b.variation_value.toLowerCase()]

    if (aWeight < bWeight) {
      return -1
    }
    if (aWeight > bWeight) {
      return 1
    }
    return 0
  })
}

/**
 * Determines if a given product has Sleeper option or not
 * @param {object} product
 * @returns {boolean}
 */
export const hasSleeperOptionAvailable = ({ category, upgrade_items }, region) => {
  if (category === 'livingroom') {
    return upgrade_items?.some(
      item => item?.title?.toLowerCase().includes('sleeper') && item?.catalog_availability[region],
    )
  }
  return false
}

/**
 * Creates a string containing Extras and Size options:
 * @param {object} product
 * @param {object} variations
 * @returns {string}
 */
export const productExtraAndVariantTag = (product, variations, includeShippingText = true, region) => {
  const extraAndVariantOptions = []
  if (!product) return ''
  const moreRoomOptionsAvailable = determineMoreRoomOptionsAvailable(product, region)
  const hasSleeperOption = hasSleeperOptionAvailable(product, region)

  if (hasSleeperOption && moreRoomOptionsAvailable) {
    extraAndVariantOptions.push('More Rooms, Sleeper Options Available')
  } else if (hasSleeperOption) {
    extraAndVariantOptions.push('Sleeper Option Available')
  } else if (moreRoomOptionsAvailable) {
    extraAndVariantOptions.push('More Room Options Available')
  } else if (product?.configOptions?.tableType > 0) {
    const isDiningRoomSet = product?.sub_category.find(subC => subC === 'Dining Room Sets')
    if (isDiningRoomSet) {
      extraAndVariantOptions.push(
        `${product?.configOptions?.tableType} Table Option${product?.configOptions?.tableType > 1 ? 's' : ''}`,
      )
    }
  }

  if (variations?.size) {
    if (product.category !== 'mattress bedding') {
      // product.category !== 'mattress bedding' is probably some kind of business logic I was not able to get answers to, keep it as legacy from other site snippets
      extraAndVariantOptions.push('More Sizes Available')
    }
  }

  if (includeShippingText && product.free_shipping) {
    extraAndVariantOptions.push('<span class="free-shipping-tag">Free Shipping</span>')
    return extraAndVariantOptions.join('<br/>')
  }

  return extraAndVariantOptions.join(', ')
}

/**
 * Creates a string containing Label options for sectionals:
 * @param {object} product
 */
export const sectionalExtra = product => {
  const sectionalExtraLabel = []
  const isSectional = product?.sub_category?.find(subC => subC === 'Sectionals')
  const arrangements = product?.configOptions?.arrangement
  const sleepers = product?.configOptions?.sleeper
  const consoles = product?.configOptions?.console

  if (!product) return ''

  if (isSectional) {
    if (arrangements > 0) {
      const hasSleeperOrConsole = sleepers > 0 || consoles > 0
      const arrangementEndLabel = !hasSleeperOrConsole
        ? `Arrangement Option${arrangements > 1 ? 's' : ''}`
        : `Arrangement${arrangements > 1 ? 's' : ''}`

      sectionalExtraLabel.push(`${arrangements} ${arrangementEndLabel}`)
    }

    if (sleepers > 0) {
      sectionalExtraLabel.push('Sleeper Options')
    }

    if (consoles > 0) {
      sectionalExtraLabel.push(`${consoles} Console Option${consoles > 1 || arrangements > 0 ? 's' : ''}`)
    }
  }

  return sectionalExtraLabel.join(', ')
}

/**
 * Checks if product is dropshipping
 *Shipping Types:
    D - adult furniture delivered by Rooms To Go trucks
    K - kids furniture delivered by Rooms To Go trucks
    C - corporate delivery (furniture delivered to showrooms)
    X - exports delivery (furniture we are sending overseas to return)
    Z - store pickup (either mattress pads or something purchased at an outlet)
    U - UPS delivery (UPS will deliver some items like chairs and tables nationwide)
    O - vendor delivery (aka drop-ship delivery; this is when a 3rd party supplier of rugs (for example) will send the item directly to the customer)
    T - giftcards
    E - express delivery (used in conjunction with types D and K)
    P - warehouse pickup (a customer can choose to pick up the furniture from a warehouse (not a showroom) if they live close by)
 * @param {object} product
 * @returns {boolean}
 */
export const isDropShipping = product => product?.delivery_type === DELIVERY_TYPES.O

/**
 * Checks if product has free shipping
 * @param {object} product
 * @returns {boolean}
 */
export const isFreeShipping = product => product?.free_shipping === true

export function getEnhancedRelationshipByProperty(enhancedRelationships, property, defaultValue = '') {
  return (
    (enhancedRelationships && enhancedRelationships?.find(item => item?.groupName === property)?.customVal) ||
    defaultValue
  )
}

export function topSelectedProduct(sku, products) {
  return products?.sort((x, y) => {
    if (x?.sku === sku) return -1
    if (y?.sku === sku) return 1
    return 0
  })
}
/**
 * Mutates reclining option labels into desired string for display
 * @param {options}
 * @returns {array}
 */
export function filterOptionLabels(options) {
  return options?.map(op => {
    if (op.label === 'power reclining') {
      op.label = `single ${op.label}`.replace(' reclining', '')
      return op
    }
    if (op.label) op.label = op?.label?.replace(' reclining', '')
    return op
  })
}

/**
 * Parse console labels from product title
 * Some ModularTwo products allow console mixing, but don't have both console types in the data (upgrade_items),
 * so we must read title to determine which labels should be shown in the description
 * @param {string} prodTitle
 * @returns {string}
 */
export const parseLabelsFromTitle = prodTitle => {
  const labels = ['storage', 'media', 'wood top'].filter(op => prodTitle.toLowerCase().includes(op))
  return labels
}

/**
 * Returns calculated price of console option compared to base product
 * @param {object} op
 * @param {number} basePrice
 * @returns {number}
 */
export const calculateOptionPrice = (op, basePrice) => op.pdp_price - basePrice

/**
 * Determines button description based on console option label
 * @param {string} label
 * @param {string} productTitle
 * @returns {string}
 */
export const consoleDescriptions = (label, productTitle) => {
  const labels = parseLabelsFromTitle(productTitle)
  switch (label) {
    case 'storage console':
      return 'Storage drawer & tabletop'
    case 'media console':
      return 'Storage plus bluetooth speakers & charging station'
    case 'wood top console':
      return 'Swivel-top wood cabinet with reading light and storage'
    case 'mixed consoles':
      return `1 ${labels[0]} & 1 ${labels[1]} console`
    default:
      return label
  }
}

const OUT_OF_STOCK_MESSAGES = ['Out of Stock ', 'Not available in your region ', 'Discontinued ', 'Available Soon ']
export function checkIfOutOfStockByStockMessage(stockMessage) {
  return typeof stockMessage === 'string'
    ? OUT_OF_STOCK_MESSAGES.includes(stockMessage)
    : !!stockMessage?.find(item => OUT_OF_STOCK_MESSAGES.includes(item?.message))
}

export const stockMessageArrayToStringMessage = stockMessageArray => {
  if (!Array.isArray(stockMessageArray)) {
    return stockMessageArray
  }
  return stockMessageArray?.reduce((messages, item) => [...messages, item?.message || ''], []).join('')
}

/**
 * Parse sentences and warranty from product description paragraph
 * @param {string} description
 * @param {array} warrantyDescription
 * @param {boolean} isMattress
 * @returns { object }
 */
export function parseDescription(description, warrantyDescription, isMattress) {
  if (!description)
    return {
      bullets: null,
      warranty: null,
    }

  let sentences = decodeTrademarkEntity(description)
    .split(/([.!?])\s+/) // Split on punctuation followed by spaces
    .reduce((acc, val, i, arr) => {
      if (i % 2 === 0 && i < arr.length - 1) {
        acc.push(val + (arr[i + 1] || '')) // Combine every two elements (sentence and punctuation)
      } else if (i === arr.length - 1 && val) {
        acc.push(val) // Handle the last element if it's a sentence without trailing punctuation
      }
      return acc
    }, [])
    .filter(Boolean) // Filter out any empty strings
    .map(sentence => sentence.trim()) // Trim leading spaces from sentences
  if (isMattress) sentences = sentences.map(sen => sen.replace('.', ''))
  const hasWarranty = sentences.find(sen => sen.toLowerCase().includes('warranty'))

  return {
    bullets: sentences.filter(sen => sen !== hasWarranty),
    warranty: hasWarranty || (warrantyDescription?.length && warrantyDescription),
  }
}

/**
 * Determine More Room Options Available Label
 * If a product has room configurations for the given region,
 * and there's at least one option that isn't the current sku.
 * We don't apply this label to single item rooms.
 * @param {object} product
 * @param {string} region
 * @returns {boolean}
 */
export function determineMoreRoomOptionsAvailable(product, region) {
  if (product?.room_configurations?.[region]?.length && !product?.single_item_room) {
    const otherRoomOptions = product.room_configurations[region].filter(
      // rc will either be an object with a sku property, or just a string sku value (from Algolia)
      rc => rc?.sku !== product?.sku && rc !== product?.sku,
    )
    if (otherRoomOptions?.length > 0) return true
  }
  return false
}

/**
 * Returns an array of strings representing the price of a product. The price can be provided as a string
 * or a number. If the price is provided as a string, it must start with a '$' symbol. The function removes
 * any commas from the price and converts it to a number. If the price is a number, it is converted to a string
 * with two decimal places. The function then splits the price string into an array of strings, where the first
 * element is the whole number part and the second element (if it exists) is the decimal part. If the decimal part
 * is '00' or '0', the decimal part is removed. If an error occurs during the conversion or splitting of the price,
 * the function returns an array containing the string 'Invalid Price'.
 *
 * @param {string|number} _price - The price of the product.
 * @return {string[]} An array of strings representing the price of the product.
 */
export function getPriceStrings(_price) {
  try {
    let price
    if (typeof _price === 'string') {
      price = Number((_price[0] === '$' ? _price.slice(1) : _price).replace(',', '')).toFixed(2)
    } else {
      price = Number(_price).toFixed(2)
    }
    const result = price.split('.')
    if (result[1] === '00' || result[1] === '0') {
      return [result[0]]
    }
    return result
  } catch {
    return ['Invalid Price']
  }
}

export function getWarrantyPriceCalculation(selectedSlotsArray, containerSavings, childItems) {
  const containerPrice = childItems?.length
    ? childItems?.reduce((acc, item) => acc + (item?.price ?? 0), 0) ?? 0
    : (selectedSlotsArray ?? []).reduce((acc, value) => {
        const sPrice = value?.filler?.price ?? value?.price ?? 0
        return acc + sPrice
      }, 0)

  const containerPriceWithSavings = currency(containerPrice).subtract(containerSavings).value

  const discountPercentage = currency(1).subtract(currency(containerPriceWithSavings).divide(containerPrice)).value

  const itemProRataPrice = currency(containerPrice).multiply(1 - discountPercentage).value
  return currency(itemProRataPrice).multiply(0.1).value // 10% of pro-rata price set by business
}
